import React, { useState, useRef, useEffect } from 'react';
import './App.css';
// Import full energy data directly from the JSON file
import energyData from './energy-burned.json';
import scalpelCursor from './assets/scalpel.png';
// We can't directly import from public folder, we'll use a different approach

// Custom cursor component that follows the mouse
const CursorTracker = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    const updatePosition = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
      setVisible(true);
    };
    
    const handleMouseLeave = () => {
      setVisible(false);
    };
    
    const handleMouseEnter = () => {
      setVisible(true);
    };
    
    window.addEventListener('mousemove', updatePosition);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    
    return () => {
      window.removeEventListener('mousemove', updatePosition);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, []);
  
  // Define scaling factor for the image
  const scaleFactor = 1.25;
  const imageWidth = 70 * scaleFactor;
  
  return (
    <div 
      style={{
        position: 'fixed',
        // Position exactly at the cursor point
        left: position.x,
        top: position.y,
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.1s',
        willChange: 'transform',
        // Center the container on the x-axis of the cursor
        transform: 'translate(-50%, 0)'
      }}
    >
      <div style={{
        width: imageWidth,
        height: 0,
        position: 'relative',
        // No vertical offset so the top of the image is at cursor position
        marginTop: '0'
      }}>
        <img 
          src="/assets/scalpel.png" 
          alt="Custom Cursor" 
          style={{ 
            width: '100%',
            height: 'auto',
            pointerEvents: 'none',
            transform: 'scaleX(-1) rotate(30deg)',
            transformOrigin: 'center top',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0
          }} 
        />
      </div>
    </div>
  );
};

function App() {
  const [selectedDate, setSelectedDate] = useState(new Date(2024, 0, 1)); // January 1, 2024
  const [circlePosition, setCirclePosition] = useState(0);
  const [showEnergyPopup, setShowEnergyPopup] = useState(false);
  const [showHoursPopup, setShowHoursPopup] = useState(false);
  const [showHeadphonePopup, setShowHeadphonePopup] = useState(false);
  const [showHeartRatePopup, setShowHeartRatePopup] = useState(false);
  const [showRespiratoryRatePopup, setShowRespiratoryRatePopup] = useState(false);
  const [showStepsPopup, setShowStepsPopup] = useState(false);
  const [collectedItems, setCollectedItems] = useState([]); // Track collected items
  const [lightningInOriginalPosition, setLightningInOriginalPosition] = useState(true);
  const [boneInOriginalPosition, setBoneInOriginalPosition] = useState(true);
  const [airpodInOriginalPosition, setAirpodInOriginalPosition] = useState(true);
  const [heartInOriginalPosition, setHeartInOriginalPosition] = useState(true);
  const [lungInOriginalPosition, setLungInOriginalPosition] = useState(true);
  const [shoeInOriginalPosition, setShoeInOriginalPosition] = useState(true);
  const [showNoseOff, setShowNoseOff] = useState(true); // New state for nose visibility
  const [isNoseShaking, setIsNoseShaking] = useState(false); // State for nose shaking animation
  const [hoursStoodData, setHoursStoodData] = useState({});
  const [headphoneData, setHeadphoneData] = useState({});
  const [heartRateData, setHeartRateData] = useState({});
  const [respiratoryRateData, setRespiratoryRateData] = useState({});
  const [stepsData, setStepsData] = useState({});
  
  // Load data
  useEffect(() => {
    fetch('/assets/hours-stood.json')
      .then(response => response.json())
      .then(data => {
        setHoursStoodData(data);
        console.log("Loaded hours stood data:", Object.keys(data).length, "entries");
      })
      .catch(error => console.error("Error loading hours stood data:", error));
      
    fetch('/assets/headphone-exposure.json')
      .then(response => response.json())
      .then(data => {
        setHeadphoneData(data);
        console.log("Loaded headphone exposure data:", Object.keys(data).length, "entries");
      })
      .catch(error => console.error("Error loading headphone exposure data:", error));
      
    fetch('/assets/heart-rate.json')
      .then(response => response.json())
      .then(data => {
        setHeartRateData(data);
        console.log("Loaded heart rate data:", Object.keys(data).length, "entries");
      })
      .catch(error => console.error("Error loading heart rate data:", error));
      
    fetch('/assets/respiratory-rate.json')
      .then(response => response.json())
      .then(data => {
        setRespiratoryRateData(data);
        console.log("Loaded respiratory rate data:", Object.keys(data).length, "entries");
      })
      .catch(error => console.error("Error loading respiratory rate data:", error));
      
    fetch('/assets/steps.json')
      .then(response => response.json())
      .then(data => {
        setStepsData(data);
        console.log("Loaded steps data:", Object.keys(data).length, "entries");
      })
      .catch(error => console.error("Error loading steps data:", error));
  }, []);
  
  // Hide default cursor
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      * { cursor: none !important; }
      .timeline-circle { cursor: none !important; }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Refs for DOM access
  const timelineRef = useRef(null);
  const circleRef = useRef(null);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentScrollRef = useRef(0);
  const ticksRef = useRef([]);
  
  // Constants
  const BOUNDARY_PERCENT = 5; // 5% boundary limit
  const SCROLL_SPEED_FACTOR = 0.7; // Adjusted scroll speed
  const DAY_WIDTH = 15; // Reduced by ~15%
  
  // Generate exactly 366 dates starting from January 1, 2024
  const allDates = useRef((() => {
    const startDate = new Date(2024, 0, 1); // January 1, 2024
    return Array.from({ length: 366 }, (_, i) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      return date;
    });
  })());
  
      // Update tick styles manually using inline styles
  const updateTicks = () => {
    if (!timelineRef.current) return;
    
    const scrollLeft = timelineRef.current.scrollLeft;
    currentScrollRef.current = scrollLeft;
    
    // Apply offset based on circle position
    const visibleTickCount = Math.floor(timelineRef.current.clientWidth / DAY_WIDTH);
    
    // Use a simpler approach - determine index directly from scroll position
    const actualIndex = Math.floor(scrollLeft / DAY_WIDTH);
    
    // Add offset based on circle position
    const circleOffset = (circlePosition / BOUNDARY_PERCENT) * (visibleTickCount / 4);
    const effectiveCenter = actualIndex + circleOffset;
    
    // Update selected date
    const selectedIndex = Math.min(
      Math.max(0, Math.round(effectiveCenter)),
      allDates.current.length - 1
    );
    
    // Instead of calculating based on visible dates,
    // simply use the center position for the selected date
    const adjustedIndex = Math.min(
      Math.max(0, Math.round(scrollLeft / DAY_WIDTH)),
      allDates.current.length - 1
    );
    
    setSelectedDate(allDates.current[adjustedIndex]);
    
    // Update all tick elements to be uniform
    ticksRef.current.forEach(tick => {
      if (!tick) return;
      
      // Apply uniform styles to all ticks
      tick.style.height = "13px";
      tick.style.width = "1px";
      tick.style.backgroundColor = "#777";
      tick.style.opacity = 1;
      tick.style.transform = "translateY(-50%)";
    });
  };
  
  // Handle scroll event
  const handleScroll = () => {
    if (!timelineRef.current) return;
    updateTicks();
  };
  
  // Set up auto-scrolling based on circle position
  useEffect(() => {
    if (!isDragging.current || !timelineRef.current) return;
    
    const scrollInterval = setInterval(() => {
      const scrollSpeed = circlePosition * SCROLL_SPEED_FACTOR;
      
      if (Math.abs(scrollSpeed) < 0.05) return;
      
      const currentScroll = timelineRef.current.scrollLeft;
      const maxScroll = timelineRef.current.scrollWidth - timelineRef.current.clientWidth;
      const newScroll = Math.max(0, Math.min(maxScroll, currentScroll + scrollSpeed));
      
      timelineRef.current.scrollLeft = newScroll;
      updateTicks();
    }, 16);
    
    return () => clearInterval(scrollInterval);
  }, [circlePosition]);
  
  // Reset item positions when date changes
  useEffect(() => {
    if (!lightningInOriginalPosition) {
      setLightningInOriginalPosition(true);
      // Remove lightning from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'lightning'));
    }
    
    if (!boneInOriginalPosition) {
      setBoneInOriginalPosition(true);
      // Remove bone from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'bone'));
    }
    
    if (!airpodInOriginalPosition) {
      setAirpodInOriginalPosition(true);
      // Remove airpod from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'airpod'));
    }
    
    if (!heartInOriginalPosition) {
      setHeartInOriginalPosition(true);
      // Remove heart from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'heart'));
    }
    
    if (!lungInOriginalPosition) {
      setLungInOriginalPosition(true);
      // Remove lung from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'lung'));
    }
    
    if (!shoeInOriginalPosition) {
      setShoeInOriginalPosition(true);
      // Remove shoe from collected items
      setCollectedItems(collectedItems.filter(item => item !== 'shoe'));
    }
  }, [selectedDate]);
  
  useEffect(() => {
    if (!timelineRef.current) return;
    
    // Initialize timeline to show January 1, 2024
    const centerTimeline = () => {
      // Adding a small delay to ensure refs are properly set up
      setTimeout(() => {
        // Explicitly set scroll to 0
        if (timelineRef.current) {
          timelineRef.current.scrollLeft = 0;
          setSelectedDate(allDates.current[0]);
          updateTicks();
        }
      }, 100);
    };
    
    // Store references to all tick elements
    ticksRef.current = Array.from(document.querySelectorAll('.timeline-tick'));
    
    // Add scroll listener
    timelineRef.current.addEventListener('scroll', handleScroll);
    
    // Initialize
    setTimeout(centerTimeline, 100);
    window.addEventListener('resize', centerTimeline);
    
    return () => {
      if (timelineRef.current) {
        timelineRef.current.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', centerTimeline);
    };
  }, []);
  
  // Handle drag interactions
  useEffect(() => {
    // Handle pointer down
    const handlePointerDown = (e) => {
      if (e.target !== circleRef.current && !circleRef.current.contains(e.target)) return;
      
      e.preventDefault();
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      if (!clientX) return;
      
      isDragging.current = true;
      startX.current = clientX;
      
      if (circleRef.current) {
        circleRef.current.classList.add('pressed');
      }
    };
    
    // Handle pointer move
    const handlePointerMove = (e) => {
      if (!isDragging.current || !containerRef.current) return;
      
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      if (!clientX) return;
      
      const deltaX = clientX - startX.current;
      const containerWidth = containerRef.current.offsetWidth;
      const percentage = (deltaX / containerWidth) * 100;
      
      const newPosition = Math.max(-BOUNDARY_PERCENT, Math.min(BOUNDARY_PERCENT, percentage));
      setCirclePosition(newPosition);
      
      // Update ticks immediately 
      updateTicks();
    };
    
    // Handle pointer up
    const handlePointerUp = () => {
      if (!isDragging.current) return;
      
      isDragging.current = false;
      setCirclePosition(0);
      
      if (circleRef.current) {
        circleRef.current.classList.remove('pressed');
      }
      
      updateTicks();
    };
    
    // Add event listeners
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchstart', handlePointerDown, { passive: false });
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
    
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, []);
  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  // Get energy data for the selected date
  const getEnergyForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in energy data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or zeros if not found
    return energyData[dateStr] || { active: 0, basal: 0, total: 0 };
  };
  
  // Get hours stood data for the selected date
  const getHoursStoodForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in hours stood data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or 0 if not found
    return hoursStoodData[dateStr] || 0;
  };
  
  // Get headphone exposure data for the selected date
  const getHeadphoneDataForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in headphone data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or default values if not found
    return headphoneData[dateStr] || { avg_dB: 0, max_dB: 0 };
  };
  
  // Get heart rate data for the selected date
  const getHeartRateForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in heart rate data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or default values if not found
    return heartRateData[dateStr] || { avg_bpm: 0, min_bpm: 0, max_bpm: 0 };
  };
  
  // Get respiratory rate data for the selected date
  const getRespiratoryRateForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in respiratory rate data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or 0 if not found
    return respiratoryRateData[dateStr] || 0;
  };
  
  // Get steps data for the selected date
  const getStepsForSelectedDate = () => {
    // Format date as YYYY-MM-DD for lookup in steps data
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    // Return the data or 0 if not found
    return stepsData[dateStr] || 0;
  };
  
  // Energy popup component
  const EnergyPopup = () => {
    const energy = getEnergyForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header">
            <h3>Energy Burned - {formattedDate}</h3>
            <button onClick={() => {
              setShowEnergyPopup(false);
              // Add lightning to collected items if not already there
              if (!collectedItems.includes('lightning')) {
                setCollectedItems([...collectedItems, 'lightning']);
              }
              setLightningInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Total Energy</div>
              <div className="energy-value">{energy.total || 0} cal</div>
            </div>
            <div className="energy-stat">
              <div className="energy-label">Active Energy</div>
              <div className="energy-value">{energy.active || 0} cal</div>
            </div>
            <div className="energy-stat">
              <div className="energy-label">Basal Energy</div>
              <div className="energy-value">{energy.basal || 0} cal</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Hours Stood popup component
  const HoursStoodPopup = () => {
    const hoursStood = getHoursStoodForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header" style={{ backgroundColor: '#ffaa00' }}>
            <h3>Hours Stood - {formattedDate}</h3>
            <button onClick={() => {
              setShowHoursPopup(false);
              // Add bone to collected items if not already there
              if (!collectedItems.includes('bone')) {
                setCollectedItems([...collectedItems, 'bone']);
              }
              setBoneInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Total Hours Stood</div>
              <div className="energy-value">{hoursStood || 0} hours</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Headphone Exposure popup component
  const HeadphoneExposurePopup = () => {
    const exposure = getHeadphoneDataForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header" style={{ backgroundColor: '#7ca1ff' }}>
            <h3>Headphone Exposure - {formattedDate}</h3>
            <button onClick={() => {
              setShowHeadphonePopup(false);
              // Add airpod to collected items if not already there
              if (!collectedItems.includes('airpod')) {
                setCollectedItems([...collectedItems, 'airpod']);
              }
              setAirpodInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Average Volume</div>
              <div className="energy-value">{exposure.avg_dB.toFixed(1) || 0} dB</div>
            </div>
            <div className="energy-stat">
              <div className="energy-label">Maximum Volume</div>
              <div className="energy-value">{exposure.max_dB.toFixed(1) || 0} dB</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Heart Rate popup component
  const HeartRatePopup = () => {
    const heartRate = getHeartRateForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header" style={{ backgroundColor: '#ff5a5a' }}>
            <h3>Heart Rate - {formattedDate}</h3>
            <button onClick={() => {
              setShowHeartRatePopup(false);
              // Add heart to collected items if not already there
              if (!collectedItems.includes('heart')) {
                setCollectedItems([...collectedItems, 'heart']);
              }
              setHeartInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Average Heart Rate</div>
              <div className="energy-value">{heartRate.avg_bpm.toFixed(1) || 0} BPM</div>
            </div>
            <div className="energy-stat">
              <div className="energy-label">Minimum Heart Rate</div>
              <div className="energy-value">{heartRate.min_bpm || 0} BPM</div>
            </div>
            <div className="energy-stat">
              <div className="energy-label">Maximum Heart Rate</div>
              <div className="energy-value">{heartRate.max_bpm || 0} BPM</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Respiratory Rate popup component
  const RespiratoryRatePopup = () => {
    const respiratoryRate = getRespiratoryRateForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header" style={{ backgroundColor: '#ff7d7d' }}>
            <h3>Respiratory Rate - {formattedDate}</h3>
            <button onClick={() => {
              setShowRespiratoryRatePopup(false);
              // Add lung to collected items if not already there
              if (!collectedItems.includes('lung')) {
                setCollectedItems([...collectedItems, 'lung']);
              }
              setLungInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Respiratory Rate</div>
              <div className="energy-value">{respiratoryRate.toFixed(1) || 0} breaths/min</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Steps popup component
  const StepsPopup = () => {
    const steps = getStepsForSelectedDate();
    const formattedDate = selectedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    return (
      <div className="energy-popup">
        <div className="energy-popup-content">
          <div className="energy-popup-header" style={{ backgroundColor: '#FF7F00' }}>
            <h3>Steps - {formattedDate}</h3>
            <button onClick={() => {
              setShowStepsPopup(false);
              // Add shoe to collected items if not already there
              if (!collectedItems.includes('shoe')) {
                setCollectedItems([...collectedItems, 'shoe']);
              }
              setShoeInOriginalPosition(false);
            }}>×</button>
          </div>
          <div className="energy-popup-body">
            <div className="energy-stat">
              <div className="energy-label">Total Steps</div>
              <div className="energy-value">{steps.toLocaleString() || 0} steps</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle background click to toggle nose visibility and trigger shaking
  const handleBackgroundClick = () => {
    setShowNoseOff(false);
    setIsNoseShaking(true);
    
    // Set a timeout to show the nose again after 0.8 seconds (matching the animation duration)
    setTimeout(() => {
      setShowNoseOff(true);
      setIsNoseShaking(false);
    }, 800);
  };

  // Define the shaking animation CSS
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    
    // Define the shaking animation keyframes
    styleElement.innerHTML = `
      @keyframes noseShake {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        3% { transform: translate(-53%, -48%) rotate(-5deg); }
        6% { transform: translate(-47%, -52%) rotate(5deg); }
        9% { transform: translate(-55%, -49%) rotate(-8deg); }
        12% { transform: translate(-45%, -51%) rotate(8deg); }
        15% { transform: translate(-54%, -48%) rotate(-7deg); }
        18% { transform: translate(-46%, -52%) rotate(7deg); }
        21% { transform: translate(-56%, -47%) rotate(-10deg); }
        24% { transform: translate(-44%, -53%) rotate(10deg); }
        27% { transform: translate(-55%, -48%) rotate(-9deg); }
        30% { transform: translate(-45%, -52%) rotate(9deg); }
        33% { transform: translate(-56%, -47%) rotate(-10deg); }
        36% { transform: translate(-44%, -53%) rotate(10deg); }
        39% { transform: translate(-57%, -46%) rotate(-12deg); }
        42% { transform: translate(-43%, -54%) rotate(12deg); }
        45% { transform: translate(-56%, -47%) rotate(-11deg); }
        48% { transform: translate(-44%, -53%) rotate(11deg); }
        51% { transform: translate(-57%, -46%) rotate(-12deg); }
        54% { transform: translate(-43%, -54%) rotate(12deg); }
        57% { transform: translate(-55%, -48%) rotate(-9deg); }
        60% { transform: translate(-45%, -52%) rotate(9deg); }
        63% { transform: translate(-54%, -49%) rotate(-7deg); }
        66% { transform: translate(-46%, -51%) rotate(7deg); }
        69% { transform: translate(-53%, -49%) rotate(-6deg); }
        72% { transform: translate(-47%, -51%) rotate(6deg); }
        75% { transform: translate(-52%, -49%) rotate(-5deg); }
        78% { transform: translate(-48%, -51%) rotate(5deg); }
        81% { transform: translate(-51%, -49%) rotate(-4deg); }
        84% { transform: translate(-49%, -51%) rotate(4deg); }
        87% { transform: translate(-51%, -50%) rotate(-3deg); }
        90% { transform: translate(-49%, -50%) rotate(3deg); }
        93% { transform: translate(-50%, -50%) rotate(-2deg); }
        96% { transform: translate(-50%, -50%) rotate(1deg); }
        100% { transform: translate(-50%, -50%) rotate(0deg); }
      }
      
      .nose-shaking {
        animation: noseShake 0.8s ease-in-out;
      }
    `;
    
    // Add the style element to the document head
    document.head.appendChild(styleElement);
    
    // Clean up on component unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add this useEffect to the existing useEffect section
  useEffect(() => {
    const updateItemPositions = () => {
      // Get reference to the background image
      const backgroundImage = document.getElementById('operationBackground');
      if (!backgroundImage) return;
      
      // Get the actual displayed dimensions of the image
      const imgRect = backgroundImage.getBoundingClientRect();
      const containerRect = backgroundImage.parentElement.getBoundingClientRect();
      
      // Calculate actual image displayed dimensions
      const imgRatio = backgroundImage.naturalWidth / backgroundImage.naturalHeight;
      
      // Only apply scaling if needed
      const itemsContainer = document.querySelector('.items-container');
      if (!itemsContainer) return;
      
      // Set the container to exactly match the displayed image size
      itemsContainer.style.width = `${imgRect.width}px`;
      itemsContainer.style.height = `${imgRect.height}px`;
      
      // Position the container to align with the image
      const offsetLeft = (containerRect.width - imgRect.width) / 2;
      const offsetTop = (containerRect.height - imgRect.height) / 2;
      
      itemsContainer.style.left = `${offsetLeft}px`;
      itemsContainer.style.top = `${offsetTop}px`;
    };
    
    // Initial positioning
    updateItemPositions();
    
    // Update on window resize
    window.addEventListener('resize', updateItemPositions);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', updateItemPositions);
    };
  }, []);

  return (
    <div className="App">
      <CursorTracker />
      {showEnergyPopup && <EnergyPopup />}
      {showHoursPopup && <HoursStoodPopup />}
      {showHeadphonePopup && <HeadphoneExposurePopup />}
      {showHeartRatePopup && <HeartRatePopup />}
      {showRespiratoryRatePopup && <RespiratoryRatePopup />}
      {showStepsPopup && <StepsPopup />}
      <div className="content-wrapper">
        <div className="game-container" style={{ 
          display: 'flex', 
          position: 'relative',
          width: '100%',
          justifyContent: 'center'
        }}>
          {/* Main image container - centered on x-axis */}
          <div className="image-container" style={{ position: 'relative' }}>
            <img 
              id="operationBackground"
              src="/assets/rana_operation1.png" 
              alt="Operation Game" 
              onClick={handleBackgroundClick}
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: 'calc(80vh)',
                objectFit: 'contain',
                display: 'block',
                marginBottom: '-30px',
                cursor: 'pointer',
              }}
            />
            
            {/* Container for all positioned items */}
            <div
              className="items-container"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              {/* Lightning bolt positioned on top of the main image */}
              {lightningInOriginalPosition && (
                <div className="item-wrapper" data-target="lightning" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/light_3d.png"
                    alt="Lightning Bolt"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEnergyPopup(true);
                    }}
                    style={{
                      transform: 'scaleX(-1) rotate(15deg)',
                      width: '25px',
                      height: 'auto',
                      cursor: 'pointer',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}
              
              {/* Bone positioned on top of the main image */}
              {boneInOriginalPosition && (
                <div className="item-wrapper" data-target="bone" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/bone_3d.png"
                    alt="Bone"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHoursPopup(true);
                    }}
                    style={{
                      transform: 'rotate(-5deg)',
                      width: '46px',
                      height: 'auto',
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.3))'
                    }}
                  />
                </div>
              )}
              
              {/* AirPod positioned on top of the main image */}
              {airpodInOriginalPosition && (
                <div className="item-wrapper" data-target="airpod" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/airpod_3d.png"
                    alt="AirPod"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHeadphonePopup(true);
                    }}
                    style={{
                      transform: 'rotate(15deg)',
                      width: '27px',
                      height: 'auto',
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.3))'
                    }}
                  />
                </div>
              )}
              
              {/* Heart positioned on top of the main image */}
              {heartInOriginalPosition && (
                <div className="item-wrapper" data-target="heart" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/heart_3d.png"
                    alt="Heart"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHeartRatePopup(true);
                    }}
                    style={{
                      transform: 'rotate(12deg)',
                      width: '30px',
                      height: 'auto',
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.3))'
                    }}
                  />
                </div>
              )}
              
              {/* Lung positioned on top of the main image */}
              {lungInOriginalPosition && (
                <div className="item-wrapper" data-target="lung" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/lung_3d.png"
                    alt="Lung"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRespiratoryRatePopup(true);
                    }}
                    style={{
                      transform: 'rotate(-10deg)',
                      width: '50px',
                      height: 'auto',
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.3))'
                    }}
                  />
                </div>
              )}
              
              {/* Shoe positioned on top of the main image */}
              {shoeInOriginalPosition && (
                <div className="item-wrapper" data-target="shoe" style={{ pointerEvents: 'auto' }}>
                  <img 
                    src="/assets/shoe_3d.png"
                    alt="Shoe"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStepsPopup(true);
                    }}
                    style={{
                      transform: 'rotate(13deg)',
                      width: '45px',
                      height: 'auto',
                      cursor: 'pointer',
                      filter: 'drop-shadow(0 0 5px rgba(0,0,0,0.3))'
                    }}
                  />
                </div>
              )}
              
              {/* Nose images - both in the same wrapper for perfect alignment */}
              <div className="item-wrapper" data-target="nose" style={{ pointerEvents: 'auto' }}>
                {/* Nose ON image - always rendered but may be covered by nose_off */}
                <img 
                  src="/assets/nose_on.png"
                  alt="Nose On"
                  className={isNoseShaking ? 'nose-shaking' : ''}
                  style={{
                    width: '25px',
                    height: 'auto',
                    zIndex: 15,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                
                {/* Nose OFF image - conditionally visible on top of nose_on */}
                {showNoseOff && (
                  <img 
                    src="/assets/nose_off.png"
                    alt="Nose Off"
                    style={{
                      width: '25px',
                      height: 'auto',
                      zIndex: 16,
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Vertical slots panel - positioned to the side */}
          <div className="collection-panel" style={{
            width: '70px',
            height: '350px',
            backgroundColor: 'white',
            borderRadius: '15px',
            position: 'absolute',
            marginLeft: '500px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: '10px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            border: '2px solid #ddd'
          }}>
            {/* Generate 6 empty slots */}
            {Array.from({ length: 6 }).map((_, index) => {
              // Check if this slot should show an item
              const isLightningSlot = !lightningInOriginalPosition && 
                                    collectedItems.includes('lightning') && 
                                    index === 0;
              
              const isBoneSlot = !boneInOriginalPosition && 
                               collectedItems.includes('bone') && 
                               index === 1;
                               
              const isAirpodSlot = !airpodInOriginalPosition && 
                                 collectedItems.includes('airpod') && 
                                 index === 2;
                                 
              const isHeartSlot = !heartInOriginalPosition && 
                                collectedItems.includes('heart') && 
                                index === 3;
                                
              const isLungSlot = !lungInOriginalPosition && 
                               collectedItems.includes('lung') && 
                               index === 4;
                               
              const isShoeSlot = !shoeInOriginalPosition && 
                               collectedItems.includes('shoe') && 
                               index === 5;
                                    
              return (
                <div 
                  key={index} 
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    border: '2px dashed #aaa',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    pointerEvents: isLightningSlot || isBoneSlot || isAirpodSlot || isHeartSlot || isLungSlot || isShoeSlot ? 'auto' : 'none' // Only allow clicks when there's content
                  }}>
                  {isLightningSlot && (
                    <img 
                      src="/assets/light_3d.png"
                      alt="Collected Lightning Bolt"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowEnergyPopup(true);
                      }}
                      style={{
                        width: '30px',
                        height: 'auto',
                        transform: 'scaleX(-1) rotate(15deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                  
                  {isBoneSlot && (
                    <img 
                      src="/assets/bone_3d.png"
                      alt="Collected Bone"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowHoursPopup(true);
                      }}
                      style={{
                        width: '35px',
                        height: 'auto',
                        transform: 'rotate(-5deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                  
                  {isAirpodSlot && (
                    <img 
                      src="/assets/airpod_3d.png"
                      alt="Collected AirPod"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowHeadphonePopup(true);
                      }}
                      style={{
                        width: '30px',
                        height: 'auto',
                        transform: 'rotate(15deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                  
                  {isHeartSlot && (
                    <img 
                      src="/assets/heart_3d.png"
                      alt="Collected Heart"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowHeartRatePopup(true);
                      }}
                      style={{
                        width: '30px',
                        height: 'auto',
                        transform: 'rotate(-5deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                  
                  {isLungSlot && (
                    <img 
                      src="/assets/lung_3d.png"
                      alt="Collected Lung"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowRespiratoryRatePopup(true);
                      }}
                      style={{
                        width: '32px',
                        height: 'auto',
                        transform: 'rotate(5deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                  
                  {isShoeSlot && (
                    <img 
                      src="/assets/shoe_3d.png"
                      alt="Collected Shoe"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event from bubbling up
                        setShowStepsPopup(true);
                      }}
                      style={{
                        width: '35px',
                        height: 'auto',
                        transform: 'rotate(10deg)',
                        objectFit: 'contain',
                        cursor: 'pointer' // Add pointer cursor to indicate it's clickable
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      
        <div className="timeline-container" ref={containerRef}>
          <div className="timeline-viewport">
            <div className="timeline" ref={timelineRef}>
              <div className="timeline-slider" style={{ paddingLeft: '500px', paddingRight: '500px' }}>
                {allDates.current.map((date, index) => (
                  <div 
                    key={index}
                    data-index={index}
                    className="timeline-tick"
                                      style={{ 
                    left: `${index * DAY_WIDTH}px`,
                    top: '50%',
                    position: 'absolute',
                    height: '13px',
                    width: '1px',
                    backgroundColor: '#777',
                    transform: 'translateY(-50%)'
                  }}
                  >
                  </div>
                ))}
              </div>
            </div>
            <div 
              className={`timeline-circle ${isDragging.current ? 'pressed' : ''}`}
              ref={circleRef}
                          style={{
              position: 'absolute',
              top: '50%',
              left: `calc(50% + ${circlePosition}%)`,
              transform: 'translate(-50%, -50%)',
              width: '42px', /* Fixed size instead of conditional */
              height: '42px',
              backgroundColor: '#fff',
              borderRadius: '50%',
              boxShadow: isDragging.current ? '0 4px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'transform 0.1s ease, box-shadow 0.1s ease',
              zIndex: 999, /* Always high z-index to ensure it stays on top */
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDragging.current ? 'grabbing' : 'grab',
              border: '1px solid black'
            }}
            >
                          <span style={{
              fontSize: '10px', 
              color: 'black', 
              fontWeight: 'bold', 
              textTransform: 'uppercase', 
              marginBottom: '-2px',
              transition: 'font-size 0.1s ease'
            }}>
              {selectedDate.toLocaleDateString('en-US', {month: 'short'}).toUpperCase()}
            </span>
            <span style={{
              fontSize: '19px', 
              color: 'black', 
              fontWeight: 'bold', 
              lineHeight: '1',
              transition: 'font-size 0.1s ease'
            }}>
              {selectedDate.getDate()}
            </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;